import { Checkbox, Chip, Stack, Table, Tooltip, Typography } from '@mui/joy';
import { Theme } from '@mui/joy/styles';
import { useTranslation } from 'react-i18next';

import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { Status } from 'core/elements';
import { formatDate } from 'core/utils';
import { ConsumerJobStatus } from 'dashboard/enums';
import { IConsumerJob } from 'dashboard/types';
import { Color, Direction, Level, Size, Variant } from 'theme/enums';
import { IColorMap } from 'theme/types';

import { isJobStuck, STUCK_AFTER_HOURS } from './utils';

// Running is the state an operator is looking for before restarting; queued is inert.
const statusColorMap: IColorMap = {
  [ConsumerJobStatus.QUEUED]: Color.NEUTRAL,
  [ConsumerJobStatus.RUNNING]: Color.PRIMARY,
};

// Styled to read as one of the app's AG Grid tables, since that is what every other
// list here looks like. Same tokens the grid theme itself uses (AgGridTable/style.css):
// --joy-palette-ag-header-backgroundColor for the header band and the neutral outlined
// border for the rules, so light and dark mode follow the grid automatically.
// No per-column menu buttons: this table has no sort or filter, and a button that opens
// nothing would be worse than none.
const tableSx = {
  tableLayout: 'auto',
  '& thead th': {
    backgroundColor: (theme: Theme) => theme.palette.ag.header.backgroundColor,
    borderRight: (theme: Theme) => `1px solid ${theme.palette.neutral.outlinedBorder}`,
    fontWeight: 500,
  },
  '& thead th:last-of-type': { borderRight: 'none' },
  '& th, & td': { padding: '6px 10px' },
};

interface IProps {
  jobs: IConsumerJob[];
  onToggleAll: (checked: boolean) => void;
  onToggleJob: (jobId: number) => void;
  selectedJobIds: number[];
}

export const ConsumerJobsTable = ({ jobs, onToggleAll, onToggleJob, selectedJobIds }: IProps) => {
  const { t } = useTranslation();

  if (!jobs.length) {
    return (
      <Typography level={Level.BODY_SM} sx={{ color: 'text.tertiary' }}>
        {t('messageConsumerRestartNoJobs')}
      </Typography>
    );
  }

  // The early return above guarantees jobs is non-empty.
  const allSelected = selectedJobIds.length === jobs.length;

  // A Joy Table rather than the shared AG Grid client table, deliberately. The list is
  // short, read-mostly and needs no sorting or filtering, which is the job architecture.md
  // scopes the grid to. More decisively, the grid deselects rows on most data updates, so
  // under the 30-second poll a single status flip would wipe the selection this table
  // exists to collect. AgGridTable also hard-codes a 300px minHeight, and its selection
  // actions only render with hasTopActions, which drags a whole toolbar under the heading.
  // If this ever needs sorting or filtering, /table-client is the path.
  return (
    <Table size={Size.SM} stickyHeader sx={tableSx}>
      <thead>
        <tr>
          <th style={{ width: 48 }}>
            <Checkbox
              aria-label={t('labelSelectAllJobs')}
              checked={allSelected}
              // Indeterminate rather than unchecked on a partial selection, so the
              // header reflects the rows instead of silently offering "select all"
              // when some are already picked.
              indeterminate={!allSelected && selectedJobIds.length > 0}
              onChange={(event) => onToggleAll(event.target.checked)}
              size={Size.SM}
            />
          </th>
          <th>{t('labelName')}</th>
          <th>{t('labelStatus')}</th>
          <th>{t('labelEnvironment')}</th>
          <th>{t('labelCreatedAt')}</th>
          <th>{t('labelStartedAt')}</th>
        </tr>
      </thead>

      <tbody>
        {jobs.map((job) => {
          const { createdAt, env, id, name, stale, startedAt, status } = job;

          return (
            <tr key={id}>
              <td>
                <Checkbox
                  aria-label={t('labelSelectJob', { name })}
                  checked={selectedJobIds.includes(id)}
                  onChange={() => onToggleJob(id)}
                  size={Size.SM}
                />
              </td>

              <td>{name}</td>

              <td>
                <Stack alignItems="center" direction={Direction.ROW} spacing={1}>
                  <Status
                    colorMap={statusColorMap}
                    labelMap={{
                      [ConsumerJobStatus.QUEUED]: t('labelConsumerJobStatus_queued'),
                      [ConsumerJobStatus.RUNNING]: t('labelConsumerJobStatus_running'),
                    }}
                    value={status}
                  />

                  {/* Running with no live consumer: an orphan, not work in flight.
                    Called out so nobody reads an eleven-month-old row as active. */}
                  {stale && (
                    <Tooltip title={t('tooltipConsumerJobStale')}>
                      <Chip color={Color.WARNING} size={Size.SM} variant={Variant.SOFT}>
                        {t('labelStale')}
                      </Chip>
                    </Tooltip>
                  )}

                  {/* Age, which the status alone hides: a months-old row reads as a
                    perfectly normal "Queued" until you look at the date column. */}
                  {isJobStuck(job) && (
                    <Tooltip title={t('tooltipConsumerJobStuck', { hours: STUCK_AFTER_HOURS })}>
                      <Chip color={Color.DANGER} size={Size.SM} variant={Variant.SOFT}>
                        {t('labelStuck')}
                      </Chip>
                    </Tooltip>
                  )}
                </Stack>
              </td>

              {/* Blank for queued jobs and orphans — see IConsumerJob.env. */}
              <td>{env ?? DEFAULT_PLACEHOLDER}</td>

              <td>{formatDate(createdAt ?? undefined, true)}</td>

              <td>{formatDate(startedAt ?? undefined, true)}</td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};
